import type { Profile } from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { fetchGmailAccounts } from "../../lib/gmail";
import { useAsync } from "../../lib/hooks";
import { fetchCompanySettings, setNotifyFromProfile } from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { useSession } from "../../session";

/**
 * Whose mailbox carries a tag its author cannot send.
 *
 * Tagging somebody in a note emails them, out of the mailbox of the person who
 * wrote it (ADR 0066). An agent has no mailbox and never will, so a note it
 * writes tags people in the data and tells none of them — which is exactly what
 * happened when Creed tagged Lysa on job #1024.
 *
 * This picks the mailbox that carries those. Only somebody who has connected
 * Google can be chosen, because the mail leaves through their account: choosing
 * a name that cannot send is the same silent failure one layer up.
 *
 * "Nobody" is a real choice and the way to switch it off. The notification is
 * then not sent, and the agent is told so rather than reporting a success
 * nobody received.
 */
export function NoteTagMailbox() {
  const { isAdmin } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const query = useAsync(async () => {
    const [profiles, accounts, settings] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, full_name, active")
        .order("sort_order")
        .order("full_name"),
      fetchGmailAccounts(),
      fetchCompanySettings(),
    ]);
    return {
      profiles:
        unwrap<Pick<Profile, "id" | "full_name" | "active">[]>(profiles),
      connected: new Set(accounts.map((a) => a.profile_id)),
      chosen: settings?.notify_from_profile_id ?? null,
    };
  }, []);

  if (query.loading) return <div className="stub">{t("Checking…")}</div>;

  const profiles = query.data?.profiles ?? [];
  const connected = query.data?.connected ?? new Set<string>();
  const chosen = query.data?.chosen ?? null;

  // Only somebody who can actually send. A person with no connection cannot
  // carry the mail, so offering them would be offering a setting that fails
  // later and somewhere else.
  const options = profiles.filter(
    (person) => person.active !== false && connected.has(person.id),
  );

  const chosenPerson = profiles.find((person) => person.id === chosen) ?? null;

  async function choose(value: string) {
    setSaving(true);
    setError(null);
    try {
      await setNotifyFromProfile(value || null);
      await query.reload();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : String(problem));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="rrow" style={{ marginTop: 24 }}>
        <span className="k">{t("Tags written by an agent")}</span>
        <span className="v">
          <span className={`badge ${chosenPerson ? "approved" : "draft"}`}>
            {chosenPerson
              ? t("from {name}", { name: chosenPerson.full_name ?? "" })
              : t("not sent")}
          </span>
        </span>
      </div>

      <ErrorNote error={error} />

      <p className="na-hint" style={{ marginTop: 10, maxWidth: 520 }}>
        {t(
          "An agent has no mailbox of its own, so a note it writes tags people and tells nobody. Pick whose Gmail carries those. The same mailbox emails the rep, and everyone ticked for every signed contract, when a customer signs; with nobody picked, those emails are not sent either.",
        )}
      </p>

      {isAdmin ? (
        options.length === 0 ? (
          <div className="shelf">
            <b>{t("Nobody has connected Gmail")}</b>
            {t("Connect an account under Tools → Integrations first.")}
          </div>
        ) : (
          <select
            className="addrow"
            disabled={saving}
            value={chosen ?? ""}
            onChange={(event) => void choose(event.target.value)}
          >
            <option value="">{t("Nobody — do not send them")}</option>
            {options.map((person) => (
              <option key={person.id} value={person.id}>
                {person.full_name}
              </option>
            ))}
          </select>
        )
      ) : (
        <div className="na-hint" style={{ maxWidth: 520 }}>
          {t("Ask an admin to change this.")}
        </div>
      )}

      {chosenPerson && !connected.has(chosenPerson.id) && (
        <div className="shelf">
          <b>
            {t("{name} is no longer connected", {
              name: chosenPerson.full_name ?? "",
            })}
          </b>
          {t(
            "Nothing is being sent. Reconnect that account, or pick somebody else.",
          )}
        </div>
      )}
    </>
  );
}
